import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { updateEmailPreferences } from '../api/email-preferences.api';
import { EMAIL_PREFERENCES_QUERY_KEY } from './useEmailPreferences';

const S = UI_STRINGS.EMAIL_PREFERENCES;

/**
 * Save the optional-email preference (FR-164).
 *
 * Deliberately NOT optimistic: nothing is written to the cache until the server
 * answers, and what is written is the server's own response. A failed save
 * therefore leaves the last saved value showing, and the card renders the error
 * inline next to the control (ui-guidelines.md section 12: the surface stays
 * open), so there is no error toast here - only a success confirmation.
 */
export function useUpdateEmailPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (optedOut: boolean) => updateEmailPreferences(optedOut),
    onSuccess: (saved) => {
      queryClient.setQueryData(EMAIL_PREFERENCES_QUERY_KEY, saved);
      toast.success(saved.optedOut ? S.SAVED_OPTED_OUT : S.SAVED_OPTED_IN);
    },
    onError: (error) => Sentry.captureException(error),
  });
}
